import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";

const LoginModal = ({ onClose }) => {
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]); // ✅ 4 digit OTP

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [seconds, setSeconds] = useState(30);
  const [canResend, setCanResend] = useState(false);

  /* ================= TIMER ================= */
  useEffect(() => {
    if (step !== 2) return;

    if (seconds <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setTimeout(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [seconds, step]);

  /* ================= SEND OTP ================= */
  const sendOtp = async () => {
    try {
      if (!email) return toast.error("Enter email");

      setLoading(true);

      await axios.post("https://mediapi.onlinepharmaque.in/api/v1/loginuser", {
        email,
      });

      toast.success("OTP Sent 📩");
      setStep(2);
      setSeconds(30);
      setCanResend(false);
    } catch {
      toast.error("Failed ❌");
    } finally {
      setLoading(false);
    }
  };

  /* ================= VERIFY OTP ================= */
  const verifyOtp = async () => {
    const finalOtp = otp.join("");

    if (finalOtp.length !== 4) {
      return toast.error("Enter full OTP");
    }

    try {
      setLoading(true);

      const res = await axios.post(
        "https://mediapi.onlinepharmaque.in/api/v1/verifyOtp",
        { email, otp: finalOtp }
      );

      if (res.data.success) {
        login(res.data.user, res.data.token);
        toast.success("Login Success 🎉");
        onClose();
      }
    } catch {
      toast.error("Invalid OTP ❌");
    } finally {
      setLoading(false);
    }
  };

  /* ================= OTP CHANGE ================= */
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose} // ✅ click outside close
    >
      <div
        className="w-[700px] h-[420px] bg-white rounded-2xl overflow-hidden shadow-2xl flex"
        onClick={(e) => e.stopPropagation()} // prevent close inside click
      >
        {/* LEFT SIDE */}
        <div className="w-1/2 bg-gradient-to-br from-[#187685] to-[#0f4f59] text-white flex flex-col justify-center items-center p-6">
          <h2 className="text-2xl font-bold mb-3">Welcome 👋</h2>
          <p className="text-sm text-center opacity-90">
            Login to access your orders
          </p>

          <img
            src="https://cdn-icons-png.flaticon.com/512/295/295128.png"
            alt="login"
            className="w-28 mt-6"
          />
        </div>

        {/* RIGHT SIDE */}
        <div className="w-1/2 p-8 flex flex-col justify-center relative">
          
          {/* ❌ CLOSE ICON */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-lg"
          >
            ✖
          </button>

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-semibold mb-4">
                Login / Signup
              </h2>

              <input
                type="email"
                placeholder="Enter Email"
                className="border p-2 rounded-lg mb-4 focus:ring-2 focus:ring-[#187685] outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendOtp();
                }}
              />

              <button
                onClick={sendOtp}
                className="bg-[#187685] text-white py-2 rounded-lg hover:bg-[#145e6b] transition"
              >
                {loading ? "Sending..." : "Continue"}
              </button>
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-semibold mb-4">
                Enter OTP
              </h2>

              <div className="flex justify-between mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) =>
                      handleOtpChange(e.target.value, index)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") verifyOtp();
                    }}
                    className="w-12 h-12 text-center border rounded-lg text-lg focus:ring-2 focus:ring-[#187685]"
                  />
                ))}
              </div>

              <button
                onClick={verifyOtp}
                className="bg-[#187685] text-white py-2 rounded-lg hover:bg-[#145e6b]"
              >
                {loading ? "Verifying..." : "Verify"}
              </button>

              <div className="text-sm mt-2 text-center">
                {canResend ? (
                  <button
                    onClick={sendOtp}
                    className="text-[#187685]"
                  >
                    Resend OTP
                  </button>
                ) : (
                  <p>Resend in {seconds}s</p>
                )}
              </div>
            </>
          )}

          {/* ✅ BEAUTIFUL CANCEL BUTTON */}
          <button
            onClick={onClose}
            className="mt-3 w-full py-2 rounded-lg border border-gray-300 text-gray-600 font-medium 
            hover:bg-red-50 hover:text-red-500 hover:border-red-400 
            transition duration-200 hover:scale-[1.02]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;